import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface UseKeyProtectionOptions {
  onBeforeUnload?: () => void
  onProtectionDisabled?: () => void
}

interface KeyLossWarningConfig {
  message: string
  proceedButtonText: string
  cancelButtonText?: string
  onProceed: () => void
  onCancel?: () => void
}

interface UseKeyProtectionReturn {
  isProtected: boolean
  enableProtection: () => void
  disableProtection: () => void
  onKeysCopied: () => void
  showKeyLossWarning: (config: KeyLossWarningConfig) => void
}

export const useKeyProtection = (options: UseKeyProtectionOptions = {}): UseKeyProtectionReturn => {
  const [isProtected, setIsProtected] = useState(false)
  const hasCopiedKeys = useRef(false)
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = useRef(location.pathname)
  const { setModelActive, toggle, setWidth } = useActiveModelStore()

  const enableProtection = () => {
    setIsProtected(true)
    hasCopiedKeys.current = false
  }

  const disableProtection = () => {
    setIsProtected(false)
    hasCopiedKeys.current = false
    options.onProtectionDisabled?.()
  }

  const onKeysCopied = () => {
    hasCopiedKeys.current = true
    disableProtection()
  }

  const showKeyLossWarning = ({
    message,
    proceedButtonText,
    cancelButtonText = 'Cancel',
    onProceed,
    onCancel,
  }: KeyLossWarningConfig) => {
    const preventNavigation = () => {
      const activeElement = document.activeElement as HTMLElement

      if (activeElement) {
        activeElement.blur()
      }
    }

    setModelActive(ModelControlEnum.NAVIGATION_WARNING, {
      title: 'Warning: Generated Keys',
      modalHeader: {
        title: 'Warning',
        showCloseButton: true,
      },
      message,
      proceedButtonText,
      cancelButtonText,
      onProceed: () => {
        onProceed()
      },
      onCancel: () => {
        onCancel?.()
        preventNavigation()
      },
    })
    setWidth('max-w-[450px]')
    toggle()
  }

  const showNavigationWarning = (onConfirm: () => void, onCancel?: () => void) => {
    showKeyLossWarning({
      message:
        'The generated keys will be lost if you leave this page. Are you sure you want to leave?',
      proceedButtonText: 'Leave Page',
      cancelButtonText: 'Stay on Page',
      onProceed: onConfirm,
      onCancel,
    })
  }

  useEffect(() => {
    if (isProtected && !hasCopiedKeys.current && location.pathname !== currentPath.current) {
      navigate(currentPath.current, { replace: true })

      showNavigationWarning(
        () => {
          disableProtection()
          navigate(location.pathname)
        },
        () => {},
      )
    }

    currentPath.current = location.pathname
  }, [location.pathname, isProtected, navigate])

  useEffect(() => {
    if (!isProtected) return

    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const link =
        target.closest('a') || target.closest('[role="button"]') || target.closest('button')

      if (link && hasCopiedKeys.current === false) {
        const href = link.getAttribute('href')
        const isNavigationLink = href && (href.startsWith('/') || href.startsWith('http'))

        if (isNavigationLink) {
          event.preventDefault()
          event.stopPropagation()

          showNavigationWarning(
            () => {
              disableProtection()
              if (href.startsWith('/')) {
                navigate(href)
              } else {
                window.open(href, '_blank')
              }
            },
            () => {},
          )
        }
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.altKey && event.key === 'F4') ||
        (event.ctrlKey && event.key === 'w') ||
        (event.ctrlKey && event.key === 'W')
      ) {
        if (hasCopiedKeys.current) return

        event.preventDefault()
        event.stopPropagation()

        showNavigationWarning(
          () => {
            options.onBeforeUnload?.()
          },
          () => {},
        )
      }
    }

    document.addEventListener('click', handleLinkClick, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('click', handleLinkClick, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isProtected, location.pathname])

  useEffect(() => {
    if (!isProtected) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasCopiedKeys.current) return

      const message =
        'You have generated keys that will be lost if you leave this page. Are you sure you want to leave?'

      event.preventDefault()
      event.returnValue = message

      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isProtected, options])

  return {
    isProtected,
    enableProtection,
    disableProtection,
    onKeysCopied,
    showKeyLossWarning,
  }
}
